import { formatDateLocal } from "./date";
import { getChartTheme } from "./chartTheme";

const getQuarterLabel = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed)) {
    const year = parsed.getFullYear();
    const quarter = Math.floor(parsed.getMonth() / 3) + 1;
    const month = String(quarter * 3).padStart(2, "0");
    return `${year}-${month}`;
  }

  if (typeof value === "string") {
    const [yearStr, monthStr] = value.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr);
    if (Number.isFinite(year) && Number.isFinite(month)) {
      const quarter = Math.floor((month - 1) / 3) + 1;
      const quarterMonth = String(quarter * 3).padStart(2, "0");
      return `${year}-${quarterMonth}`;
    }
  }

  return null;
};

const buildQuarterLabels = (endDate = new Date(), years = 3) => {
  const labels = [];
  let year = endDate.getFullYear();
  let quarter = Math.floor(endDate.getMonth() / 3) + 1;

  for (let i = 0; i < years * 4; i += 1) {
    const month = String(quarter * 3).padStart(2, "0");
    labels.unshift(`${year}-${month}`);
    quarter -= 1;
    if (quarter === 0) {
      quarter = 4;
      year -= 1;
    }
  }
  return labels;
};

const buildQuarterSeries = (points, labels) => {
  if (!Array.isArray(points) || points.length === 0) {
    return labels.map(() => 0);
  }

  const sorted = [...points].sort((a, b) => new Date(a.date) - new Date(b.date));
  const map = new Map();

  sorted.forEach((item) => {
    const label = getQuarterLabel(item.date);
    if (!label) return;
    map.set(label, item.shares);
  });

  return labels.map((label) => (map.has(label) ? map.get(label) : 0));
};

const getInfoCount = (info) => {
  if (!info) return null;
  if (typeof info === "object" && Number.isFinite(info.num)) {
    return info.num;
  }
  if (typeof info !== "string") return null;
  try {
    const normalized = info.split("'").join("\"");
    const parsed = JSON.parse(normalized);
    return Number.isFinite(parsed?.num) ? parsed.num : null;
  } catch (error) {
    return null;
  }
};

const getInfoProfit = (info) => {
  if (!info) return null;
  if (typeof info === "object" && Number.isFinite(info.profit)) {
    return info.profit;
  }
  if (typeof info !== "string") return null;
  try {
    const normalized = info.split("'").join("\"");
    const parsed = JSON.parse(normalized);
    return Number.isFinite(parsed?.profit) ? parsed.profit : null;
  } catch (error) {
    return null;
  }
};

const buildHoverChartOptions = (isDarkMode, unit = "", xTickFormatter = null, yTickFormatter = null) => {
  const theme = getChartTheme(isDarkMode);
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: theme.tooltipBg,
        titleColor: theme.tooltipTitle,
        bodyColor: theme.tooltipBody,
        borderColor: theme.tooltipBorder,
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        ticks: {
          display: true,
          color: theme.tickColor,
          font: { size: 10 },
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          padding: 6,
          callback: xTickFormatter || undefined,
        },
        grid: { display: false },
        border: {
          color: theme.borderColor,
        },
      },
      y: {
        ticks: {
          color: theme.tickColor,
          callback: (value) => {
            const formatted = yTickFormatter ? yTickFormatter(Number(value).toFixed(2)) : Number(value).toFixed(2);
            return unit ? `${formatted}${unit}` : formatted;
          },
        },
        grid: { color: theme.gridColor },
        border: {
          color: theme.borderColor,
        },
      },
    },
  };
};

export {
  buildQuarterLabels,
  buildQuarterSeries,
  buildHoverChartOptions,
  formatDateLocal,
  getInfoCount,
  getInfoProfit,
};
